import arriereBackoffice, {
  ListingKind,
  OnlineTravelAgency,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { DistributionListingsSyncStatusParams } from '@new/domains/properties/screens/viewMode/ViewMode.types'

const coHostRelationFragment = {
  coHostRelation: {
    type: true,
    account: {
      id: true,
      name: true,
    },
  },
}

const getHomeDistributionData = async (homeId?: string, agency?: OnlineTravelAgency) => {
  if (homeId === undefined || agency === undefined) {
    return undefined
  }

  return arriereBackoffice.queries
    .distribution({
      __alias: 'getHomeDistributionData',
      getHomeListing: {
        __args: { homeId, agency },
        homeId: true,
        title: true,
        externalHomeId: true,
        updatedAt: true,
        agencyAccount: {
          id: true,
          channelAccountId: true,
          market: true,
        },
        ...coHostRelationFragment,
      },
    })
    .then(result => {
      return result.getHomeListing
    })
}

export const getHomeInfoForSyncStatus = async (homeId?: string, agency?: OnlineTravelAgency) => {
  return arriereBackoffice.queries.home
    .raw({
      __alias: 'homeForSyncStatus',
      __args: { homeId },
      id: true,
      name: true,
      hotelId: true,
      region: {
        id: true,
        name: true,
      },
      thumbnail: {
        url: true,
      },
      links: {
        manual: true,
        listings: {
          leadSource: true,
          url: true,
        },
      },
    })
    .then(async ({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      const homeDistribution = await getHomeDistributionData(homeId, agency).catch(() => undefined)

      return {
        ...data,
        homeDistribution,
      }
    })
}

export const getRoomTypeInfoForSyncStatus = async (
  hotelRoomTypeId: string,
  agency?: OnlineTravelAgency,
) => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'homeForSyncStatus',
      get: {
        __args: { hotelRoomTypeId },
        hotelId: true,
        hotelRoomTypeId: true,
        listingLinks: {
          leadSource: true,
          url: true,
        },
        name: true,
        thumbnail: {
          url: true,
        },
      },
    })
    .then(async ({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      const homeDistribution = await getHomeDistributionData(hotelRoomTypeId, agency)

      // Map data to match getHomeInfoForSyncStatus
      return {
        ...data.get,
        id: data.get?.hotelRoomTypeId ?? hotelRoomTypeId,
        name: data.get?.name ?? '',
        links: {
          manual: '',
          listings: data.get?.listingLinks,
        },
        region: undefined,
        homeDistribution,
      }
    })
}

export const homeDistributionStatusFragment = (agency: OnlineTravelAgency) => ({
  category: true,
  status: {
    author: {
      name: true,
      kind: true,
    },
    startTime: true,
    updatedAt: true,
    endTime: true,
    errors: {
      code: true,
      internalCategory: true,
      reason: true,
      ts: true,
      resolution: {
        __args: { agency },
        priority: true,
        description: true,
        instructions: true,
      },
      reportedBy: true,
    },
    warnings: {
      code: true,
      internalCategory: true,
      reason: true,
      ts: true,
      resolution: {
        __args: { agency },
        priority: true,
        description: true,
        instructions: true,
      },
      reportedBy: true,
    },
    status: true,
  },
})

export const getHomeDistributionsAndListings = async (
  homeId: string,
  agency: OnlineTravelAgency,
) => {
  return arriereBackoffice.queries.distribution
    .raw({
      __alias: 'getHomeDistributionsAndListings',
      distributedListing: {
        __args: {
          listingId: homeId,
        },
        distributions: {
          __args: {
            activeOnly: false,
            agency,
          },
          active: true,
          agencyAccount: {
            agency: true,
            channel: true,
            market: true,
            notes: true,
          },
          externalHomeId: true,
          homeId: true,
          homeTitle: true,
          lastUpdateAt: true,
          status: true,
          statuses: {
            ...homeDistributionStatusFragment(agency),
          },
        },
        listingLinks: {
          leadSource: true,
          url: true,
        },
        listings: {
          __args: { agency },
          active: true,
          agencyAccount: {
            agency: true,
          },
          ...coHostRelationFragment,
          propertyType: true,
          propertyTypeGroup: true,
        },
      },
    })
    .then(async ({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data
    })
}

export const getHomeSyncStatusTimeline = async (homeId: string, agency: OnlineTravelAgency) => {
  return arriereBackoffice.queries.distribution
    .raw({
      __alias: 'getHomeSyncStatusTimeline',
      distributedListing: {
        __args: {
          listingId: homeId,
        },
        distributionIssuesHistory: {
          __args: { agency },
          listingId: true,
          category: true,
          isSuccess: true,
          issues: {
            description: true,
            isError: true,
            isWarning: true,
            reportedBy: true,
            code: true,
            resolution: {
              __args: { agency },
              priority: true,
              description: true,
              instructions: true,
              errorMessage: true,
            },
          },
          resolvedBy: {
            kind: true,
            name: true,
          },
          time: true,
          issueDurationInSeconds: true,
          issueStartedAt: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data.distributedListing?.distributionIssuesHistory
    })
}

export const getDistributionIssueMetrics = async (ota: OnlineTravelAgency) => {
  return arriereBackoffice.queries.distribution
    .raw({
      __alias: 'getDistributionIssueMetrics',
      distributionIssueMetrics: {
        __args: { ota },
        listingsWithIssuesCount: true,
        listingsWithIssuesSince: {
          threshold: true,
          count: true,
          thresholdUnit: true,
        },
        listingsWithNewIssuesOn: {
          date: true,
          count: true,
        },
        listingsWithResolvedIssuesOn: {
          date: true,
          count: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data.distributionIssueMetrics
    })
}

export const getDistributionListingsWithStats = async ({
  pagination,
  regionIds,
  name,
  withOffboarded,
  sort,
  agency,
  onlyWithDownListings,
  otaFilters,
  listingKind,
}: DistributionListingsSyncStatusParams) => {
  const otaWithDownListings =
    onlyWithDownListings && agency ? { otaWithDownListings: [agency] } : {}

  return arriereBackoffice.queries.distribution
    .raw({
      __alias: 'getDistributionListingsWithStats',
      distributionListings: {
        __args: {
          pagination,
          regionIds,
          name,
          withOffboarded,
          sort,
          otaFilters,
          ...(listingKind?.length ? { listingKind: listingKind[0] as ListingKind } : {}),
          ...otaWithDownListings,
        },
        total: true,
        results: {
          id: true,
          referenceHomeId: true,
          name: true,
          listingKind: true,
          active: true,
          listed: true,
          address: { regionId: true },
          region: { name: true },
          lifecycle: {
            listingStatus: true,
            guestReadinessStatus: true,
            accountStatus: true,
          },
          roomsCount: true,
          bathroomsCount: true,
          maxOccupancy: true,
          building: { name: true },
          distributionStatusIssuesStats: {
            __args: { agency },
            homeId: true,
            agency: true,
            amenitiesErrors: true,
            amenitiesWarnings: true,
            availabilityErrors: true,
            availabilityWarnings: true,
            dateOfIssue: true,
            discountsErrors: true,
            discountsWarnings: true,
            feesAndTaxesErrors: true,
            feesAndTaxesWarnings: true,
            listingContentErrors: true,
            listingContentWarnings: true,
            photosErrors: true,
            photosWarnings: true,
            pricingErrors: true,
            pricingWarnings: true,
            priority: true,
            roomsErrors: true,
            roomsWarnings: true,
          },
          distributions: {
            __args: {
              agency,
              activeOnly: true,
            },
            active: true,
            agencyAccount: {
              agency: true,
              channel: true,
              market: true,
              notes: true,
            },
            status: true,
            statuses: {
              category: true,
              status: {
                author: { name: true, kind: true },
                startTime: true,
                updatedAt: true,
                endTime: true,
                errors: { code: true, internalCategory: true, reason: true, ts: true },
                warnings: { code: true, internalCategory: true, reason: true, ts: true },
                status: true,
              },
            },
            lastUpdateAt: true,
          },
          listings: {
            __args: { agency },
            active: true,
            distributionSettings: {
              airbnbSettings: { permitId: true },
            },
            agencyAccount: {
              agency: true,
              channel: true,
              channelAccountId: true,
              id: true,
              market: true,
            },
            ...coHostRelationFragment,
            apiConnected: true,
            channels: { active: true, listingId: true, listingLink: true, ota: true },
            externalHomeId: true,
            title: true,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data.distributionListings
    })
}
